import React, { useCallback, useMemo } from 'react';

import { Header as LayoutComponentHeader } from '@vtblife/layout-components';

import { graphql } from '@search/gql-client/src/useGql';
import { useGql2Loader } from '@search/gql-client/src/useGql2';
import { useGqlContext } from '@search/gql-client/src/gqlContext';
import { useRouter } from '@search/offer-search-routes/src';
import { errorCatcherWrap } from '@search/error/src/catcher';
import { useM2AuthContext } from '@search/auth/src/M2AuthProvider';
import classname from '@search/classname/src';
import { NewBuildingCardRoute } from '@search/nb-routes/src/NewBuildingCardRoute';
import { NewBuildingConstructionRoute } from '@search/nb-routes/src/NewBuildingConstructionRoute';
import { NewBuildingSearchRoute } from '@search/nb-routes/src/NewBuildingSearchRoute';
import { DeveloperSearchRoute } from '@search/nb-routes/src/DeveloperSearchRoute';
import { DeveloperLandingRoute } from '@search/nb-routes/src/DeveloperLandingRoute';
import { DeveloperRoute } from '@search/nb-routes/src/DeveloperRoute';
import { NewBuildingSearchMapRoute } from '@search/nb-routes/src/NewBuildingSearchMapRoute';
import { Shared } from '@search/whitelabel-search-www/types/shared';

import { dataLayerPush } from '../../../../domain/google/dataLayerPush';
import { getDeveloperPromoName } from '../../../../domain/newbuilding/developer-promo/helpers';
import { getDeveloperBannerGtmData } from '../../../../domain/newbuilding/developer-promo/analytics';
import { DeveloperWithPromoName } from '../../../../domain/newbuilding/developer-promo/types';

import type { HeaderQuery$variables as HeaderQueryVariables, HeaderQuery$data as HeaderQueryResponse } from './__generated__/HeaderQuery.graphql';
import './styles.css';

const POSSIBLE_ACTIVE_PARTNERS_SET = new Set([
    'samolet'
]);

const cn = classname.bind(null, 'Header');

const headerQuery = graphql`
    query HeaderQuery($regionId: Int) {
        header(regionId: $regionId) {
            items {
                id
                displayName
                path
                icon
                dropdownMenu {
                    id
                    displayName
                    url
                    icon
                    dropdownMenu {
                        id
                        displayName
                        url
                    }
                }
            }
            partners {
                samolet {
                    id
                    logo
                    logoWhite
                    url
                }
            }
            personalButtons {
                favorites {
                    professionalUrl,
                    url
                }
                offerPlacement {
                    professionalUrl,
                    authorizedUrl,
                    url
                }
                savedSearch {
                    professionalUrl,
                    url
                }
            }
            loginPaths {
                default {
                    id
                    url
                }
                client {
                    id
                    url
                }
                partner {
                    id
                    url
                }
                professional {
                    id
                    url
                }
            }
        }
        sidebar(regionId: $regionId) {
            items {
                id
                displayName
                path
                url
                icon
                showInDesktopProfileMenu
                dropdownMenu {
                    id
                    displayName
                    path
                    url
                    icon
                    showInDesktopProfileMenu
                }
            }
            footerItems {
                id
                displayName
                path
                url
                icon
                showInDesktopProfileMenu
            }
        }
    }
`;

export function useHeaderFetcher() {
    const gql = useGqlContext();

    return React.useCallback(
        (vars: HeaderQueryVariables, opts?: { signal?: AbortSignal }) => gql.client<
            HeaderQueryVariables,
            HeaderQueryResponse
        >(headerQuery, vars, opts)
        , [ gql ]
    );
}

export function useHeaderLoader() {
    return useGql2Loader(useHeaderFetcher(), { cachePrefix: 'Header', authTrack: true });
}

const Header: React.FC<{
    buildLink?: (url: string) => string;
    regionId?: number;
    developerPromoRegionId?: number;
    isFullWidth?: boolean;
    isBordered?: boolean;
}> = ({
    buildLink,
    regionId,
    developerPromoRegionId,
    isFullWidth = true,
    isBordered
}) => {
    const { auth } = useM2AuthContext();
    const headerLoader = useHeaderLoader();
    const { data: dataRaw } = headerLoader({ regionId });
    const dataRef = React.useRef(dataRaw);

    if (dataRaw) dataRef.current = dataRaw;
    const data = dataRef.current;

    // @ts-ignore
    const router: Shared.RouterType = useRouter();
    const developerWithPromoNameByRegion = useMemo<DeveloperWithPromoName | undefined>(() => {
        const resultPartner = getDeveloperPromoName(developerPromoRegionId);

        // @ts-expect-error
        return POSSIBLE_ACTIVE_PARTNERS_SET.has(resultPartner) ? resultPartner : undefined;
    }, [ developerPromoRegionId ]);
    const partners = useMemo(() => {
        return Object.fromEntries(Object.entries(data?.header.partners ?? {}).filter(([ key, value ]) => POSSIBLE_ACTIVE_PARTNERS_SET.has(key) && value));
    }, [ data?.header.partners ]);

    const pageType = useMemo(() => {
        const { route } = router.resolve([
            NewBuildingSearchRoute,
            NewBuildingSearchMapRoute,
            DeveloperSearchRoute,
            NewBuildingCardRoute,
            NewBuildingConstructionRoute,
            DeveloperLandingRoute,
            DeveloperRoute
        ]);

        switch (route.displayName) {
            case 'NewBuildingCardRoute':
            case 'NewBuildingConstructionRoute':
                return 'card';
            case 'DeveloperLandingRoute':
                return 'landing';
            case 'NewBuildingSearchRoute':
            case 'NewBuildingSearchMapRoute':
            case 'DeveloperSearchRoute':
            case 'DeveloperRoute':
                return 'serp';
            default:
                return;
        }
    }, [ router ]);

    const onPartnerClick = useCallback((partnerName: string) => {
        const dataObj = pageType && getDeveloperBannerGtmData(partnerName, {
            action: 'click',
            section: 'logo',
            pageType
        });

        dataObj && dataLayerPush(dataObj);
    }, [ pageType ]);

    const currentRole = auth.user?.currentRoleType;

    const getUrl = (url?: string | null) => buildLink && url?.startsWith('/') ? buildLink(url) : url;

    const items = data?.header?.items?.map(item => ({
        ...item,

        path: getUrl(item?.path),

        dropdownMenu: item.dropdownMenu?.map(dropDownItem => ({
            ...dropDownItem,
            url: getUrl(dropDownItem?.url),
            id: dropDownItem.id + (dropDownItem.url ?? '0'),
            dropdownMenu: dropDownItem.dropdownMenu?.map(subDropdownItem => ({
                ...subDropdownItem,
                url: getUrl(subDropdownItem?.url),
                id: subDropdownItem.id + (subDropdownItem.url ?? '0')
            }))
        }))
    })) ?? [];

    return (
        <div className={cn()}>
            <LayoutComponentHeader
                isColored
                isBordered={isBordered}
                isFullWidth={isFullWidth}
                cdnUrl='https://cdn.m2.ru'
                loginPaths={data?.header.loginPaths}
                items={items}
                partners={partners}
                pathname={router.location.pathname}
                activePartner={developerWithPromoNameByRegion}
                authorizedItems={data?.sidebar}
                roleType={currentRole}
                logoutPath={`/logout?retpath=${encodeURIComponent(router.currentUrl)}`}
                onPartnerClick={onPartnerClick}
                personalButtons={data?.header?.personalButtons}
            />
        </div>
    );
};

export default errorCatcherWrap(Header);
